/**
 * Modal for managing tree member access
 */

import { useState } from "react";
import { useVaultContext } from "../../context/VaultContext";
import { useVaultMembers } from "../../hooks/useVault";
import {
  useAddTreeMember,
  useRemoveTreeMember,
  useTreeMembers,
  useUpdateTreeMember,
} from "../../hooks/useTreeSharing";
import LoadingSpinner from "../Common/LoadingSpinner";

interface TreeSharingModalProps {
  treeId: number;
  treeName: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function TreeSharingModal({
  treeId,
  treeName,
  isOpen,
  onClose,
}: TreeSharingModalProps) {
  const { currentVaultId } = useVaultContext();
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRole, setSelectedRole] = useState<"TREE_ADMIN" | "TREE_VIEWER">("TREE_VIEWER");
  const [error, setError] = useState<string | null>(null);

  const { data: treeMembers = [], isLoading: treeMembersLoading } = useTreeMembers(treeId);
  const { data: vaultMembers = [], isLoading: vaultMembersLoading } =
    useVaultMembers(currentVaultId);
  const addMemberMutation = useAddTreeMember(treeId);
  const updateMemberMutation = useUpdateTreeMember(treeId);
  const removeMemberMutation = useRemoveTreeMember(treeId);

  if (!isOpen) return null;

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedUserId) {
      setError("Please select a user");
      return;
    }

    try {
      await addMemberMutation.mutateAsync({
        user_id: selectedUserId,
        role: selectedRole,
      });

      setSelectedUserId("");
      setSelectedRole("TREE_VIEWER");
      setShowAddMember(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add member");
    }
  };

  const handleChangeRole = async (userId: string, newRole: "TREE_ADMIN" | "TREE_VIEWER") => {
    if (confirm("Are you sure you want to change this member's role?")) {
      await updateMemberMutation.mutateAsync({ userId, data: { role: newRole } });
    }
  };

  const handleRemoveMember = async (userId: string, email: string | null) => {
    const displayEmail = email || 'this member';
    if (confirm(`Are you sure you want to remove ${displayEmail} from this tree?`)) {
      await removeMemberMutation.mutateAsync(userId);
    }
  };

  // Filter out users who are already tree members
  const availableVaultMembers = vaultMembers.filter(
    (vm) => !treeMembers.some((tm) => tm.user_id === vm.user_id)
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-vault-400 hover:text-vault-600"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="h-6 w-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Content */}
        <h2 className="font-serif text-2xl font-semibold text-vault-900 mb-2">
          Share Tree
        </h2>
        <p className="text-sm text-vault-600 mb-6">
          Manage access to <strong>{treeName}</strong>
        </p>

        <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700">
          <strong>Note:</strong> Only vault members can be added to trees. Members
          must be invited to the vault first.
        </div>

        {/* Add Member Section */}
        {showAddMember ? (
          <form onSubmit={handleAddMember} className="mb-6 rounded-lg border border-vault-200 bg-vault-50 p-4">
            <h3 className="font-medium text-vault-900 mb-3">Add Member</h3>

            {error && (
              <div className="mb-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                {error}
              </div>
            )}

            {vaultMembersLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner size="sm" />
              </div>
            ) : availableVaultMembers.length === 0 ? (
              <p className="text-sm text-vault-500 mb-3">
                All vault members have already been added to this tree.
              </p>
            ) : (
              <>
                <div className="mb-3">
                  <label htmlFor="userId" className="block text-sm font-medium text-vault-700 mb-1">
                    Select Member
                  </label>
                  <select
                    id="userId"
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full rounded-md border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                    required
                  >
                    <option value="">-- Select a member --</option>
                    {availableVaultMembers.map((member) => (
                      <option key={member.user_id} value={member.user_id}>
                        {member.user_email} {member.user_name && `(${member.user_name})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label htmlFor="role" className="block text-sm font-medium text-vault-700 mb-1">
                    Role
                  </label>
                  <select
                    id="role"
                    value={selectedRole}
                    onChange={(e) =>
                      setSelectedRole(e.target.value as "TREE_ADMIN" | "TREE_VIEWER")
                    }
                    className="w-full rounded-md border border-vault-300 px-3 py-2 text-sm focus:border-vault-500 focus:outline-none focus:ring-1 focus:ring-vault-500"
                  >
                    <option value="TREE_VIEWER">Viewer (Read-only)</option>
                    <option value="TREE_ADMIN">Admin (Full Access)</option>
                  </select>
                </div>
              </>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddMember(false);
                  setError(null);
                  setSelectedUserId("");
                }}
                className="rounded-md border border-vault-300 px-4 py-2 text-sm font-medium text-vault-700 hover:bg-vault-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={addMemberMutation.isPending || availableVaultMembers.length === 0}
                className="rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {addMemberMutation.isPending ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Adding...
                  </>
                ) : (
                  "Add Member"
                )}
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setShowAddMember(true)}
            className="mb-6 rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700 transition-colors flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-4 w-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
            Add Member
          </button>
        )}

        {/* Members List */}
        <div>
          <h3 className="font-medium text-vault-900 mb-3">Current Members</h3>

          {treeMembersLoading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : treeMembers.length === 0 ? (
            <p className="text-center text-vault-500 py-8">No members with direct access yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-vault-200 text-left text-sm text-vault-600">
                    <th className="pb-3 font-medium">Email</th>
                    <th className="pb-3 font-medium">Name</th>
                    <th className="pb-3 font-medium">Role</th>
                    <th className="pb-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {treeMembers.map((member, idx) => (
                    <tr
                      key={member.user_id}
                      className={`text-sm ${
                        idx % 2 === 0 ? "bg-vault-50" : "bg-white"
                      } hover:bg-vault-100 transition-colors`}
                    >
                      <td className="py-3 px-2">{member.user_email}</td>
                      <td className="py-3 px-2">
                        {member.user_name || <em className="text-vault-400">Not set</em>}
                      </td>
                      <td className="py-3 px-2">
                        <span
                          className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${
                            member.role === "TREE_ADMIN"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {member.role === "TREE_ADMIN" ? "Admin" : "Viewer"}
                        </span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex gap-2">
                          <select
                            value={member.role}
                            onChange={(e) =>
                              handleChangeRole(
                                member.user_id,
                                e.target.value as "TREE_ADMIN" | "TREE_VIEWER"
                              )
                            }
                            className="text-xs rounded border border-vault-300 px-2 py-1 focus:border-vault-500 focus:outline-none"
                            disabled={updateMemberMutation.isPending}
                          >
                            <option value="TREE_VIEWER">Viewer</option>
                            <option value="TREE_ADMIN">Admin</option>
                          </select>
                          <button
                            onClick={() => handleRemoveMember(member.user_id, member.user_email)}
                            disabled={removeMemberMutation.isPending}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                            title="Remove member"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="h-4 w-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
